import {
    useState
} from "react";
import {
    ComponentHeader,
    ExampleSection,
    getSectionToc
} from "./shared";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import {
    Button,
    Badge
} from "ncore-ui-kit";
import {
    InlineCode,
    DocsTable,
    Paragraph,
    CodeBlock,
    Callout,
    Article,
    Preview,
    Section
} from "../../components/content";

const NAME = "Sticker";

const USAGE_CODE = `<Badge
    icon={CheckIcon}
    type="success"
    title="Active"
/>

<Badge
    variant="outline"
    type="warning"
    title="Draft"
/>`;

const STATUSES = [
    {
        key: "stickerPage.statusQueued",
        type: "neutral"
    },
    {
        key: "stickerPage.statusRunning",
        type: "info"
    },
    {
        key: "stickerPage.statusSucceeded",
        type: "success"
    },
    {
        key: "stickerPage.statusFailed",
        type: "danger"
    }
] as const;

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    }
], NAME, t);

const StickerPage = () => {
    const {
        t
    } = useDocsI18n();

    const [
        statusIndex,
        setStatusIndex
    ] = useState(0);

    const status = STATUSES[statusIndex] ?? STATUSES[0];

    const handleNextStatus = () => setStatusIndex((statusIndex + 1) % STATUSES.length);

    return <Article>
        <ComponentHeader
            lead={t("stickerPage.lead")}
            name={NAME}
        />
        <Callout
            title={t("stickerPage.noticeTitle")}
        >
            {t("stickerPage.noticeText")}
        </Callout>
        <DocsTable
            rows={[
                [
                    <InlineCode>
                        {"title, icon, type, variant"}
                    </InlineCode>,
                    t("stickerPage.mapSame")
                ],
                [
                    <InlineCode>
                        {"size"}
                    </InlineCode>,
                    t("stickerPage.mapSize")
                ],
                [
                    <InlineCode>
                        {"iconPosition=\"left\" | \"right\""}
                    </InlineCode>,
                    <InlineCode>
                        {"iconPosition=\"start\" | \"end\""}
                    </InlineCode>
                ],
                [
                    <InlineCode>
                        {"transition, spreadBehaviour, isDisabled"}
                    </InlineCode>,
                    t("stickerPage.mapSame")
                ],
                [
                    <InlineCode>
                        {"customColor, customTitleColor, customIconColor"}
                    </InlineCode>,
                    t("stickerPage.mapSame")
                ],
                [
                    <InlineCode>
                        {"titleStyle, style, customTheme"}
                    </InlineCode>,
                    t("stickerPage.mapSame")
                ],
                [
                    <InlineCode>
                        {"accessibilityLabel, aria-*"}
                    </InlineCode>,
                    t("stickerPage.mapSame")
                ]
            ]}
            headers={[
                t("stickerPage.mapOld"),
                t("stickerPage.mapNew")
            ]}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("stickerPage.overviewText")}
            </Paragraph>
            <Preview
                caption={t("stickerPage.overviewCaption")}
            >
                <Badge
                    spreadBehaviour="free"
                    title={t(status.key)}
                    type={status.type}
                />
                <Button
                    title={t("stickerPage.nextStatus")}
                    onPress={handleNextStatus}
                    spreadBehaviour="free"
                    variant="outline"
                />
            </Preview>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default StickerPage;
